import React, { ChangeEvent, FocusEvent } from 'react';
import getClassNames from 'classnames';
import Div from '../Element/Div/Div';
import Element from '../Element/Element';
import Flex from '../Flex/Flex';
import Icon from '../Icon/Icon';
import styles from './QuantitySelector.module.scss';
import { CreateProps } from '../../types/utils/CreateProps';

type QuantitySelectorProps = CreateProps<
  {
    /** Quantity of entities */
    value: number;
    /** Function to decrease quantity value */
    decrease: () => void;
    /** Function to increase quantity value */
    increase: () => void;
    /** Value at which decrease button disables */
    minValue?: number;
    /** Value at which increase button disables */
    maxValue?: number;
    /** Disables the whole button */
    disabled?: boolean;
    /** Function which adds possibility to type value inside the component and sends only typed natural numbers back */
    onInputChange?: (
      event: React.ChangeEvent<HTMLInputElement>,
      value: number
    ) => void;
    /** Hidden accessability label for input*/
    inputLabel?: string;
    /** Hidden accessability label for descrease button */
    decreaseButtonLabel?: string;
    /** Hidden accessability label  for increase button*/
    increaseButtonLabel?: string;
  },
  typeof Div,
  'color' | 'backgroundColor'
>;

const QuantitySelector: React.FunctionComponent<QuantitySelectorProps> = ({
  minValue = 1,
  value,
  maxValue,
  decrease,
  increase,
  disabled,
  className,
  onInputChange,
  inputLabel,
  decreaseButtonLabel,
  increaseButtonLabel,
  ...props
}) => {
  let disabledDecrease;
  let disabledIncrease;
  const classNames = getClassNames(styles.quantitySelector_input, {
    [styles.quantitySelector_input__disabled]: disabled,
  });

  const handleEmptyInput = (e: FocusEvent<HTMLInputElement>) => {
    if (isNaN(e.target.valueAsNumber)) {
      onInputChange && onInputChange(e, minValue);
    }
  };

  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    let inputValue = e.target.valueAsNumber;
    if (maxValue) {
      /* istanbul ignore else */
      if (inputValue > maxValue) {
        inputValue = maxValue;
      }
    }

    if (inputValue < minValue) {
      inputValue = minValue;
    }

    onInputChange && onInputChange(e, inputValue);
  };

  if (minValue === 0) {
    disabledDecrease = value === 0 ? styles.quantitySelector__disabled : '';
  } else {
    disabledDecrease = value === 1 ? styles.quantitySelector__disabled : '';
  }

  if (maxValue) {
    disabledIncrease =
      value === maxValue ? styles.quantitySelector__disabled : '';
  }

  if (minValue) {
    disabledDecrease =
      value === minValue ? styles.quantitySelector__disabled : '';
  }

  if (disabled) {
    disabledIncrease = styles.quantitySelector__disabled;
    disabledDecrease = styles.quantitySelector__disabled;
  }

  return (
    <Div
      hideOverflow
      className={[styles.quantitySelector, className]}
      {...props}
    >
      <Flex spaceBetween centered>
        <Element
          aria-label={decreaseButtonLabel}
          tagName="button"
          tabIndex={disabled || disabledDecrease ? -1 : 0}
          color={disabledDecrease ? 'gray400' : 'black'}
          padding={{ vertical: 'xs' }}
          clickableAppearance
          backgroundColor={disabledDecrease ? 'gray200' : 'white'}
          onClick={disabledDecrease ? () => {} : decrease}
          className={[
            'decrease-quantity-test',
            disabledDecrease,
            styles.quantitySelector_button,
            styles.quantitySelector_button__decrease,
          ]}
        >
          <Icon name="minus" size="sm" color="inherit" />
        </Element>
        <input
          aria-label={inputLabel}
          className={classNames}
          readOnly={!onInputChange}
          disabled={disabled || !onInputChange}
          value={value}
          name="value"
          type="number"
          onChange={handleInput}
          onBlur={handleEmptyInput}
        ></input>
        <Element
          aria-label={increaseButtonLabel}
          tagName="button"
          tabIndex={disabled || disabledIncrease ? -1 : 0}
          color={disabledIncrease ? 'gray400' : 'black'}
          padding={{ vertical: 'xs' }}
          clickableAppearance
          backgroundColor={disabledIncrease ? 'gray200' : 'white'}
          onClick={disabledIncrease ? () => {} : increase}
          className={[
            'increase-quantity-test',
            disabledIncrease,
            styles.quantitySelector_button,
            styles.quantitySelector_button__increase,
          ]}
        >
          <Icon name="plus" size="sm" color="inherit" />
        </Element>
      </Flex>
    </Div>
  );
};

export default QuantitySelector;
